import * as React from "react";

import FormCardContainer from "@impero-com/common/components/formCardContainer";
import RightSideIcon from "@impero-com/common/components/rightSideIcon";
import {
    withRouter,
    withParams,
} from "@impero-com/common/components/routerUtils";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import { t } from "@impero-com/common/utils/i18n";
import {
    Button,
    emitNotification,
    CardFooter,
    FormTitle,
    FormSubtitle,
} from "@impero-com/ui";
import { List, Spin } from "antd";
import { Link, NavigateFunction, Params } from "react-router-dom";

import {
    postAccessToken,
    postEnterOrg,
} from "api/generated/authentication/authentication";
import { RedirectTarget } from "api/generated/model";
import findYourTeam from "img/find-your-team.svg";

import { storeLastChosenOrg } from "../utils/storage/lastChosenOrg";

import * as theme from "./theme.css";

type OrgArrayOrNull = Org[] | null;

class TeamList extends React.Component<TeamListProps, TeamListState> {
    constructor(props: TeamListProps) {
        super(props);
        this.state = {
            data: [],
            status: { type: "INITIAL" },
        };
    }

    componentDidMount() {
        this.fetchTeams();
    }

    fetchTeams() {
        const { ajaxManager, params } = this.props;
        ajaxManager.ajax<OrgArrayOrNull, TeamListStatus>(
            () =>
                postAccessToken({
                    token: params.token ?? "",
                }),
            {
                component: this,
                initialStatusValue: "INITIAL",
                getErrorStatus: () => ({ type: "INITIAL" }),
                getSuccessStatus: (data: [OrgArrayOrNull]) => {
                    if (
                        data === null ||
                        data[0] === null ||
                        data[0] === undefined
                    ) {
                        this.props.navigate("/expired");
                        return { type: "INITIAL" };
                    } else {
                        this.setState({ data: data[0] });
                        return { type: "DEFAULT" };
                    }
                },
                inFlightStatus: { type: "LOADING" },
                onErrorCode: {
                    400: () => {
                        emitNotification("warning", t("LoginFlow_NoOrgFound"));
                    },
                },
            }
        );
    }

    handleLogin = (orgShortName: string) => {
        const { ajaxManager } = this.props;
        ajaxManager
            .ajax<RedirectTarget, TeamListStatus>(
                () =>
                    postEnterOrg({
                        org_short_name: orgShortName,
                    }),
                {
                    component: this,
                    initialStatusValue: "DEFAULT",
                    getSuccessStatus: () => ({ type: "DEFAULT" }),
                    getErrorStatus: () => ({ type: "DEFAULT" }),
                    inFlightStatus: { type: "LOGGING_IN" },
                }
            )
            .then((response: RedirectTarget) => {
                storeLastChosenOrg(orgShortName);
                window.location.href = response.redirect;
            });
    };

    render() {
        const { status } = this.state;
        return (
            <FormCardContainer
                rightSide={
                    <RightSideIcon icon={findYourTeam} customImages={false} />
                }
            >
                <FormTitle title={t("LoginFlow_YourOrganizations")} />
                <span
                    className={
                        this.state.data && !this.state.data.length
                            ? theme.formDisplayNone
                            : ""
                    }
                >
                    <FormSubtitle
                        subtitle={t("LoginFlow_FollowingOrganizationsFound")}
                    />
                </span>
                {status.type === "INITIAL" ? (
                    <Spin
                        size="large"
                        tip={t("LoginFlow_LoadingOrgList")}
                        className={theme.loadingOrgList}
                    />
                ) : (
                    <List
                        className={theme.formTeamList}
                        dataSource={this.state.data}
                        locale={{
                            emptyText: t("LoginFlow_EmailHasNoOrganization"),
                        }}
                        renderItem={(item: Org) => (
                            <List.Item key={item.short_name}>
                                <List.Item.Meta
                                    title={
                                        <>
                                            <b>{item.short_name}</b>.impero.com
                                        </>
                                    }
                                />
                                <div>
                                    <Button
                                        onClick={() =>
                                            this.handleLogin(item.short_name)
                                        }
                                        variant="primary"
                                        id={`teamLoginBtn-${item.short_name}`}
                                        className={theme.formTeamListButton}
                                        ghost
                                    >
                                        {t("LoginFlow_LoginNow")}
                                    </Button>
                                </div>
                            </List.Item>
                        )}
                    />
                )}

                <CardFooter className={theme.formTeamListFooter}>
                    <Link to="/forgot">{t("LoginFlow_DidntFindTeam")}</Link>
                </CardFooter>
            </FormCardContainer>
        );
    }
}

const TeamListWithNavigate = withRouter(withParams(TeamList));

export { TeamListWithNavigate as TeamList };
interface Org {
    name: string;
    short_name: string;
}

interface TeamListProps {
    navigate: NavigateFunction;
    params: Params;
    ajaxManager: AjaxManager;
}

type TeamListStatus =
    | { type: "INITIAL" }
    | { type: "DEFAULT" }
    | { type: "LOADING" }
    | { type: "LOGGING_IN" };
interface TeamListState {
    status: TeamListStatus;
    data?: Org[];
}
